import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  GameState,
  getNextCanvasState,
  initialGameState,
} from "../../models/game";
import { Block, BlockMouseDownEvent, BlockPositionMap, ScreenPosition, blockConfigs, zeroIOPS, ROOT_BLOCK_ID, IOPS } from "../../models/block";
import { TrafficPatterns } from "../../models/traffic-patterns";
import {
  ConnectPointClickEvent,
  ConnectorMap,
  ConnectorTypes,
  ReactFlowHandleClickEvent,
} from "../../models/connectors";
import { Connection, EdgeChange, Node, NodeChange, addEdge, applyEdgeChanges, applyNodeChanges, getConnectedEdges } from "reactflow";

// Define the initial state using that type
const initialState: GameState = initialGameState;

export const gameSlice = createSlice({
  name: "game",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    gameStart: (state) => {
      state.isStarted = true;
      state.isRunning = true;
      // Apply the configured initial IOPS to the root block
      if (state.blockIOPS[ROOT_BLOCK_ID]) {
        state.blockIOPS[ROOT_BLOCK_ID].outputIOPS = state.initialRootIOPS;
      }
    },
    gamePause: (state) => {
      state.isRunning = false;
    },
    gameResume: (state) => {
      state.isRunning = true;
    },
    gameTick: (state, action: PayloadAction<number>) => {
      state.timeElapsedInSec += action.payload;
      const nextGameState = getNextCanvasState(state);
      state.blockIOPS = nextGameState.blockIOPS;
      state.level = nextGameState.level;
      state.accountBalance = nextGameState.accountBalance;
      state.blocks = nextGameState.blocks;
    },
    blockMouseDown: (state, action: PayloadAction<BlockMouseDownEvent>) => {
      state.draggingBlock = action.payload;
      state.blocks[action.payload.blockID].isSelected = true;

      //De-select the previous block if it is not the same as the current one
      if (state.selectedBlock && state.selectedBlock !== action.payload.blockID) {
        state.blocks[state.selectedBlock].isSelected = false;
      }

      state.selectedBlock = action.payload.blockID;
    },
    blockMouseUp: (state) => {
      state.draggingBlock = null;
    },
    canvasClick: (state) => {
      //De-select the currently selected block, if any
      if (state.selectedBlock) {
        state.blocks[state.selectedBlock].isSelected = false;
      }
      state.selectedBlock = null;
    },
    blockMove: (state, action: PayloadAction<ScreenPosition>) => {
      if (state.draggingBlock) {
        const newTop = action.payload.top - state.draggingBlock.diff.top;
        const newLeft = action.payload.left - state.draggingBlock.diff.left;

        state.blocks[state.draggingBlock.blockID].top = newTop;
        state.blocks[state.draggingBlock.blockID].left = newLeft;
      }
    },
    addBlock: (state, action: PayloadAction<Block>) => {
      let newBlock = action.payload;
      newBlock.id = generateNewKey(state);
      state.blocks = {
        ...state.blocks,
        [newBlock.id]: newBlock,
      };
      state.blockIOPS[newBlock.id] = {
        blockID: newBlock.id, inputIOPS: zeroIOPS(), outputIOPS: zeroIOPS(), percentageSuccess: 100
      };
      const purchasePrice = blockConfigs[state.blocks[newBlock.id].blockType].purchasePrice;
      state.accountBalance -= purchasePrice;
      state.connectionStartBlock = null;
      const newNode: Node = {
        id: newBlock.id,
        position: { x: action.payload.left, y: action.payload.top },
        type: 'gameBlock',
        data: {
          label: blockConfigs[action.payload.blockType].displayName,
          blockType: action.payload.blockType
        },
      };
      state.nodes.push(newNode);
    },
    removeSelectedBlock: (state, action: PayloadAction<string>) => {
      if (state.isStarted) {
        const sellPrice = blockConfigs[state.blocks[action.payload].blockType].sellPrice;
        state.accountBalance += sellPrice;
      }
      else {
        const purchasePrice = blockConfigs[state.blocks[action.payload].blockType].purchasePrice;
        state.accountBalance += purchasePrice;
      }

      let newCanvasState = cleanupRelatedConnections(action.payload, state);
      let finalBlocks: BlockPositionMap = {};
      Object.values(newCanvasState.blocks).forEach((block) => {
        if (block.id !== action.payload) {
          finalBlocks = {
            ...finalBlocks,
            [block.id]: block,
          };
        }
      });

      delete newCanvasState.blockIOPS[action.payload];

      state.blocks = finalBlocks;
      state.connectors = newCanvasState.connectors;
      state.selectedBlock = null;
      state.connectionStartBlock = null;

      const nodesToDelete = [...state.nodes.filter((node) => node.id === action.payload)];
      const edgesToDelete = new Set(getConnectedEdges(nodesToDelete, state.edges).map((edge) => edge.id));
      state.edges = state.edges.filter((edge) => !edgesToDelete.has(edge.id));
      state.nodes = state.nodes.filter((node) => node.id !== action.payload);

    },
    removeSelectedEdge: (state, action: PayloadAction<string>) => {
      state.edges = state.edges.filter((edge) => edge.id !== action.payload);
    },
    connectBlock: (state, action: PayloadAction<ConnectPointClickEvent>) => {
      //if this is the first connection point clicked
      //save it for later when the second one is clicked
      if (!state.connectionStartBlock) {
        state.connectionStartBlock = action.payload;
        return;
      }

      //make sure the start block is at the input of the connector
      //end the end block is at the output
      let endBlockID = state.connectionStartBlock.blockID;
      let startBlockID = action.payload.blockID;

      if (action.payload.connectorType === ConnectorTypes.INPUT) {
        endBlockID = action.payload.blockID;
        startBlockID = state.connectionStartBlock.blockID;
      }

      // Create a new connector and link it to the blocks
      const connectorId = generateNewKey(state);

      const inputConnections =
        state.blocks[endBlockID].inputConnectors || []

      inputConnections.push(connectorId);

      const outputConnections =
        state.blocks[startBlockID].outputConnectors || []

      outputConnections.push(connectorId);

      state.connectors[connectorId] = {
        id: connectorId,
        inputBlockKey: startBlockID,
        outputBlockKey: endBlockID,
      };

      state.blocks = {
        ...state.blocks,
        [startBlockID]: {
          ...state.blocks[startBlockID],
          outputConnectors: outputConnections,
        },
        [endBlockID]: {
          ...state.blocks[endBlockID],
          inputConnectors: inputConnections,
        },
      };

      //reset connection start block for the next connection event
      state.connectionStartBlock = null;
    },
    nodesChanged: (state, action: PayloadAction<NodeChange[]>) => {
      state.nodes = applyNodeChanges(action.payload, state.nodes);
    },
    edgesChanged: (state, action: PayloadAction<EdgeChange[]>) => {
      state.edges = applyEdgeChanges(action.payload, state.edges);
    },
    onConnect: (state, action: PayloadAction<Connection>) => {
      const edge = { ...action.payload, type: 'gameEdge' };
      state.edges = addEdge(edge, state.edges);
      state.connectionStartNode = null;
    },
    nodeHandleClick: (state, action: PayloadAction<ReactFlowHandleClickEvent>) => {
      if (!state.connectionStartNode) {
        state.connectionStartNode = action.payload;
      }
    },
    connectEnd: (state) => {
      state.connectionStartNode = null;
    },
    updateInitialRootIOPS: (state, action: PayloadAction<IOPS>) => {
      state.initialRootIOPS = action.payload;
    },
    updateCurrentRootIOPS: (state, action: PayloadAction<IOPS>) => {
      if (state.blockIOPS[ROOT_BLOCK_ID]) {
        state.blockIOPS[ROOT_BLOCK_ID].outputIOPS = action.payload;
      }
    },
    updateTrafficPattern: (state, action: PayloadAction<TrafficPatterns>) => {
      state.trafficPattern = action.payload;
    }
  },
});

function generateNewKey(state: GameState) {
  let key = Math.random().toString(20).substring(2);
  while (state.blocks[key] || state.connectors[key]) {
    key = Math.random().toString(20).substring(2);
  }
  return key;
}

const deleteConnection = (
  connectionID: string,
  connectionMap: ConnectorMap
): ConnectorMap => {
  let result: ConnectorMap = {};
  Object.values(connectionMap).forEach((conn) => {
    if (conn.id !== connectionID) {
      result = { ...result, [conn.id]: conn };
    }
  });
  return result;
};

const cleanupRelatedConnections = (
  blockID: string,
  oldCanvasState: GameState
): GameState => {
  let newCanvasState = { ...oldCanvasState };
  const otherBlock = newCanvasState.blocks[blockID];
  if (otherBlock.inputConnectors) {
    Array.from(otherBlock.inputConnectors).forEach((connID) => {
      const otherBlockID = newCanvasState.connectors[connID].inputBlockKey;

      let otherOutputConnectors = [...newCanvasState.blocks[otherBlockID].outputConnectors || []]
      otherOutputConnectors = otherOutputConnectors.filter((conn) => conn !== connID);

      newCanvasState = {
        ...newCanvasState,
        blocks: {
          ...newCanvasState.blocks,
          [otherBlockID]: {
            ...newCanvasState.blocks[otherBlockID],
            outputConnectors: otherOutputConnectors,
          },
        },
      };

      newCanvasState.connectors = deleteConnection(
        connID,
        newCanvasState.connectors
      );
    });
  }

  if (otherBlock.outputConnectors) {
    Array.from(otherBlock.outputConnectors).forEach((connID) => {
      const otherBlockID = newCanvasState.connectors[connID].outputBlockKey;

      let otherInputConnectors = [...newCanvasState.blocks[otherBlockID].inputConnectors || []]
      otherInputConnectors = otherInputConnectors.filter((conn) => conn !== connID);

      newCanvasState = {
        ...newCanvasState,
        blocks: {
          ...newCanvasState.blocks,
          [otherBlockID]: {
            ...newCanvasState.blocks[otherBlockID],
            inputConnectors: otherInputConnectors,
          },
        },
      };

      newCanvasState.connectors = deleteConnection(
        connID,
        newCanvasState.connectors
      );
    });
  }
  return newCanvasState;
};

export const {
  gameStart,
  gamePause,
  gameResume,
  gameTick,
  addBlock,
  connectBlock,
  blockMouseDown,
  blockMouseUp,
  canvasClick,
  blockMove,
  removeSelectedBlock,
  removeSelectedEdge,
  nodesChanged,
  edgesChanged,
  onConnect,
  nodeHandleClick,
  connectEnd,
  updateInitialRootIOPS,
  updateCurrentRootIOPS,
  updateTrafficPattern

} = gameSlice.actions;


export default gameSlice.reducer;
